import axios from 'axios';
import { uuidv4 } from '../../utils/lodash';

// Client funnel instrumentation ([BI] spec). Every event is enveloped and
// POSTed to the platform's /events ingestion. po_placed is SERVER-side.
//
// Fire-and-forget: never blocks the UI, swallows errors (e.g. if /events
// isn't up yet). Gated by the caller (only fired in live mode with a buyer).

const EVENTS_URL = '/marketplace-api/events';

export type MktEventName =
  | 'catalog_viewed'
  | 'product_viewed'
  | 'cart_started'
  | 'cart_updated'
  | 'checkout_started';

// Stable per-tab session id.
let sessionId = '';
const getSessionId = (): string => {
  if (sessionId) return sessionId;
  try {
    const key = 'mkt_session_id';
    const existing = window.sessionStorage.getItem(key);
    if (existing) {
      sessionId = existing;
    } else {
      sessionId = uuidv4();
      window.sessionStorage.setItem(key, sessionId);
    }
  } catch {
    sessionId = uuidv4();
  }
  return sessionId;
};

export interface EmitOpts {
  merchantId: string;
  enabled: boolean;
}

export const emitMktEvent = (
  name: MktEventName,
  props: Record<string, unknown>,
  { merchantId, enabled }: EmitOpts
): void => {
  if (!enabled || !merchantId) return;
  // The platform /events DTO is forbidNonWhitelisted — ANY extra top-level key
  // (e.g. a stray `name` or top-level `ts`) → 400. Send ONLY whitelisted keys:
  // { type, merchantId?, buyerUserId?, cohortId?(ignored), isTestCohort?,
  //   sessionId?, eventId?, props? }. cohortId is resolved server-side from
  // buyerUserId, so we don't send it. Per-event timestamp rides inside `props`.
  const envelope = {
    type: name,
    // Buyer's id (same id sent in buyer headers) — the gate key.
    buyerUserId: merchantId,
    merchantId,
    sessionId: getSessionId(),
    eventId: uuidv4(),
    props: { ...props, ts: new Date().toISOString() },
  };
  // Fire-and-forget — do not await, do not surface errors.
  axios.post(EVENTS_URL, envelope).catch(() => undefined);
};
